import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, DataTable, Dialog, RadioGroup, Stack, t, Text, TextField, toast } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { api, unwrap } from '../lib/api';

type Options = maintenance.components['schemas']['Preferences'];
type FoundClient = { name: string; title: string; servers: number; favorite: boolean };

/**
 * The displayed scope of the person (SPEC-002), sent with every list (RG-002-8).
 * D75: the « Périmètre » picker is set aside for now: every list shows all the
 * clients the rights allow, whatever scope was chosen before (the rights still
 * narrow it, server-side).
 */
export function useDisplayScope(): string {
  return 'all';
}

/**
 * « Périmètre » of the TopBar (SPEC-002, F1.19): a ghost button naming the
 * displayed scope, and its Dialog. It narrows the display inside the rights;
 * it never grants anything (ADR-006).
 */
export function ScopePicker() {
  const queryClient = useQueryClient();
  const current = useDisplayScope();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState(current);
  const [search, setSearch] = useState('');
  const options = useQuery({
    queryKey: ['scope-options', search],
    queryFn: async () => unwrap(await api.maintenance.GET('/scope-options', { params: { query: { q: search || undefined } } })),
    staleTime: 30_000,
  });
  useEffect(() => { if (open) setChoice(current); }, [open, current]);

  const data = options.data;
  const count = (value: string) => data?.options.find(o => o.value === value)?.count ?? 0;
  // A person limited to one client sees its name, and only it (RG-002-8).
  const single = data?.restricted && data.total === 1 ? data.clients[0]?.title ?? data.currentLabel : null;
  const scopeLabel = (value: string) => {
    if (single) return single;
    if (value.startsWith('client:')) return data?.currentLabel ?? value.slice('client:'.length);
    return t(`scope.options.${value}`, { n: count(value) });
  };

  const favorite = async (client: FoundClient) => {
    const path = { params: { path: { client: client.name } } };
    try {
      unwrap(client.favorite
        ? await api.maintenance.DELETE('/favorites/clients/{client}', path)
        : await api.maintenance.POST('/favorites/clients/{client}', path));
      await queryClient.invalidateQueries({ queryKey: ['scope-options'] });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch {
      toast({ title: t('scope.favoriteFailed'), tone: 'neg' });
    }
  };

  const apply = async () => {
    try {
      unwrap(await api.maintenance.PUT('/preferences', { body: { displayScope: choice } as Options }));
      setOpen(false);
      // Every page reloads on the new scope.
      await queryClient.invalidateQueries();
    } catch {
      toast({ title: t('scope.saveFailed'), tone: 'neg' });
    }
  };

  const chosenClient = choice.startsWith('client:') ? data?.clients.find(c => `client:${c.name}` === choice) : undefined;
  const radios = [
    ...(['all', 'mine', 'open', 'favorites'] as const).map(value => ({
      value,
      label: t(`scope.options.${value}`, { n: count(value) }),
      description: value === 'favorites' && (data?.favorites ?? 0) === 0 ? t('scope.noFavorite') : undefined,
    })),
    ...(choice.startsWith('client:') ? [{ value: choice, label: chosenClient?.title ?? scopeLabel(choice) }] : []),
  ];

  return (
    <>
      <Button variant="ghost" size="sm" icon="filter_alt" onClick={() => setOpen(true)} disabled={Boolean(single)}>
        {t('scope.button', { scope: scopeLabel(current) })}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        size="md"
        title={t('scope.title')}
        confirmLabel={t('scope.apply')}
        cancelLabel={t('scope.cancel')}
        onConfirm={apply}
      >
        <Stack gap={16}>
          <RadioGroup label={t('scope.label')} options={radios} value={choice} onChange={setChoice} />
          <TextField
            icon="search"
            label={t('scope.search')}
            placeholder={t('scope.searchPlaceholder', { n: data?.total ?? 0 })}
            value={search}
            onChange={(e: { target: { value: string } }) => setSearch(e.target.value)}
          />
          {data && data.clients.length > 0 && (
            <DataTable
              label={search ? t('scope.results') : t('scope.favorites')}
              rows={data.clients}
              rowKey={(c: FoundClient) => `client:${c.name}`}
              toolbar={false}
              density="compact"
              height={240}
              activeRowKey={choice}
              onRowClick={(c: FoundClient) => setChoice(`client:${c.name}`)}
              columns={[
                { key: 'title', label: t('scope.columns.client'), grow: true },
                { key: 'servers', label: t('scope.columns.servers'), type: 'number' as const, width: 110 },
                {
                  key: 'favorite', label: t('scope.columns.favorite'), width: 90, sortable: false,
                  render: (c: FoundClient) => (
                    <Button variant="ghost" size="sm" icon="star" pressed={c.favorite} onClick={() => favorite(c)}
                      label={c.favorite ? t('scope.unfavorite', { client: c.title }) : t('scope.favorite', { client: c.title })} />
                  ),
                },
              ]}
            />
          )}
          {search && data && data.clients.length === 0 && <Text variant="body-sm" tone="mute">{t('scope.noResult')}</Text>}
          <Text variant="caption" tone="mute">{t('scope.note')}</Text>
        </Stack>
      </Dialog>
    </>
  );
}

/** A record opened by a link, outside the displayed scope (SPEC-002): said once, one click to show everything. */
export function OutsideScopeBanner({ kind, outside }: { kind: 'server' | 'project' | 'client' | 'application'; outside: boolean | undefined }) {
  const queryClient = useQueryClient();
  const current = useDisplayScope();
  const options = useQuery({
    queryKey: ['scope-options', ''],
    queryFn: async () => unwrap(await api.maintenance.GET('/scope-options', { params: { query: {} } })),
    enabled: Boolean(outside),
    staleTime: 30_000,
  });
  if (!outside) return null;
  const count = options.data?.options.find(o => o.value === current)?.count ?? 0;
  const label = current.startsWith('client:') ? options.data?.currentLabel ?? current : t(`scope.options.${current}`, { n: count });
  const showAll = async () => {
    try {
      unwrap(await api.maintenance.PUT('/preferences', { body: { displayScope: 'all' } as Options }));
      await queryClient.invalidateQueries();
    } catch {
      toast({ title: t('scope.saveFailed'), tone: 'neg' });
    }
  };
  return (
    <Banner tone="info" title={t(`scope.outside.${kind}`, { scope: label })}
      actions={<Button variant="secondary" size="sm" onClick={showAll}>{t('scope.showAll')}</Button>} />
  );
}
